import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  Users, Calendar, ChevronLeft, X, Plus, Download, ClipboardCheck,
  UserPlus, Settings2, FileText, CheckCircle2, Clock, Sparkles, RefreshCw,
} from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Committees, downloadAuthed } from "@/lib/api";

/**
 * Committees — top-level governance workspace.
 *
 * Three sub-views: `hub` (all 10 committee cards), `committee` (roster +
 * meetings history for one committee), and `meeting` (agenda editor +
 * attendance + action items).
 */
export default function CommitteesDialog({ open, onOpenChange }) {
  const [view, setView] = useState("hub");
  const [committees, setCommittees] = useState([]);
  const [facility, setFacility] = useState({ facility_name: "", fiscal_year_label: "" });
  const [activeCommittee, setActiveCommittee] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([Committees.list(), Committees.getFacility()]);
      setCommittees(c);
      setFacility(f);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load committees");
    }
  }, []);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const openCommittee = async (id) => {
    const c = await Committees.get(id);
    setActiveCommittee(c);
    setView("committee");
  };

  const openMeeting = async (committeeId, meetingId) => {
    const m = await Committees.getMeeting(committeeId, meetingId);
    setActiveMeeting(m);
    setView("meeting");
  };

  const goHub = () => {
    setView("hub");
    setActiveCommittee(null);
    setActiveMeeting(null);
  };
  const goCommittee = () => {
    setView("committee");
    setActiveMeeting(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1400px] w-[96vw] h-[92vh] p-0 flex flex-col overflow-hidden bg-[#f8fafc]"
        data-testid="committees-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#e2e8f0] bg-white">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-[#0284c7]" />
            <div className="flex-1">
              <DialogTitle className="font-heading text-2xl tracking-tight text-[#0f172a]">
                Committees
              </DialogTitle>
              <DialogDescription className="text-sm text-[#475569] mt-0.5">
                {facility.facility_name || "Set your facility name in settings"} · {view === "hub" ? "10 governance committees" : activeCommittee?.name}
              </DialogDescription>
            </div>
            {view !== "hub" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={view === "meeting" ? goCommittee : goHub}
                data-testid="committees-back"
                className="text-[#475569]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen(true)}
              data-testid="committees-settings-btn"
              className="text-[#475569]"
            >
              <Settings2 className="w-4 h-4 mr-1" /> Facility
            </Button>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1 rounded hover:bg-[#f1f5f9] text-[#475569]"
              aria-label="Close"
              data-testid="committees-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {view === "hub" && (
            <HubView committees={committees} onOpen={openCommittee} />
          )}
          {view === "committee" && activeCommittee && (
            <CommitteeView
              committee={activeCommittee}
              onOpenMeeting={(mid) => openMeeting(activeCommittee.id, mid)}
              onReload={reload}
            />
          )}
          {view === "meeting" && activeMeeting && activeCommittee && (
            <MeetingWorkspace
              committee={activeCommittee}
              meeting={activeMeeting}
              onReload={async () => {
                const m = await Committees.getMeeting(activeCommittee.id, activeMeeting.id);
                setActiveMeeting(m);
              }}
            />
          )}
        </div>

        <FacilitySettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          facility={facility}
          onSaved={reload}
        />
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function HubView({ committees, onOpen }) {
  const byCadence = useMemo(() => {
    const groups = { monthly: [], quarterly: [], as_required: [], annually: [] };
    for (const c of committees) (groups[c.cadence] || (groups[c.cadence] = [])).push(c);
    return groups;
  }, [committees]);

  return (
    <div className="p-6 space-y-8" data-testid="committees-hub">
      {["monthly", "quarterly", "as_required", "annually"].map((cadence) => {
        const list = byCadence[cadence] || [];
        if (!list.length) return null;
        return (
          <section key={cadence}>
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#475569] mb-3">
              {cadence.replace("_", " ")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onOpen(c.id)}
                  data-testid={`committee-card-${c.template_key}`}
                  className="text-left rounded-xl border border-[#e2e8f0] bg-white p-4 hover:border-[#0284c7] hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading font-bold text-[#0f172a] tracking-tight leading-tight">
                      {c.name}
                    </div>
                    {c.auto_inject_quality && (
                      <span className="text-[9px] uppercase font-semibold tracking-widest text-[#0284c7] bg-[#f0f9ff] rounded px-1.5 py-0.5">
                        AI
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">
                    Chair: {c.chair_role}
                  </div>
                  <div className="mt-3 flex gap-4 text-[11px] text-[#475569]">
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" /> {c.member_count} members
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {c.open_action_items} open items
                    </span>
                  </div>
                  {c.upcoming_meeting && (
                    <div className="mt-2 text-[11px] text-[#0284c7]">
                      Next: {c.upcoming_meeting.scheduled_at?.slice(0, 10)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function CommitteeView({ committee, onOpenMeeting, onReload }) {
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);

  const load = useCallback(async () => {
    const [m, mt, ai] = await Promise.all([
      Committees.listMembers(committee.id),
      Committees.listMeetings(committee.id),
      Committees.listActionItems(committee.id),
    ]);
    setMembers(m);
    setMeetings(mt);
    setActionItems(ai);
  }, [committee.id]);

  useEffect(() => {
    load();
  }, [load]);

  const quickSeedMembers = async () => {
    for (const role of committee.default_member_roles || []) {
      await Committees.createMember(committee.id, { name: `[Enter name]`, role_title: role });
    }
    toast.success(`Seeded ${committee.default_member_roles?.length || 0} role slots`);
    load();
  };

  return (
    <div className="p-6 space-y-6" data-testid="committee-view">
      <div className="rounded-xl border border-[#e2e8f0] bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-[#475569] mb-1">Purpose</div>
        <p className="text-sm text-[#0f172a]">{committee.purpose || "—"}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Members */}
        <div className="lg:col-span-2 rounded-xl border border-[#e2e8f0] bg-white">
          <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0284c7]" />
            <div className="font-heading text-base font-bold text-[#0f172a] flex-1">Members ({members.length})</div>
            {members.length === 0 && (committee.default_member_roles || []).length > 0 && (
              <Button size="sm" variant="outline" onClick={quickSeedMembers} data-testid="committee-quick-seed">
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Quick-seed roles
              </Button>
            )}
            <Button size="sm" onClick={() => setShowMemberForm(true)} data-testid="committee-add-member" className="bg-[#0284c7] hover:bg-[#0369a1] text-white">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          <div className="divide-y divide-[#f1f5f9]">
            {members.length === 0 && (
              <div className="p-6 text-center text-sm text-[#475569] italic">
                No members yet. Click <b>Quick-seed roles</b> to pre-populate the {committee.default_member_roles?.length} standard roles.
              </div>
            )}
            {members.map((m) => (
              <MemberRow key={m.id} member={m} committeeId={committee.id} onSaved={load} />
            ))}
          </div>
        </div>

        {/* Action items */}
        <div className="rounded-xl border border-[#e2e8f0] bg-white">
          <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#0284c7]" />
            <div className="font-heading text-base font-bold text-[#0f172a]">Action Items</div>
          </div>
          <div className="divide-y divide-[#f1f5f9] max-h-[420px] overflow-auto">
            {actionItems.length === 0 && (
              <div className="p-4 text-sm text-[#475569] italic">No action items.</div>
            )}
            {actionItems.map((a) => (
              <div key={a.id} className="p-3 text-sm">
                <div className="font-semibold text-[#0f172a]">{a.title}</div>
                <div className="text-xs text-[#475569] mt-0.5">
                  {a.owner_name || "—"} · due {a.due_date || "—"} ·{" "}
                  <span className={
                    a.status === "completed" ? "text-[#16a34a] font-semibold" :
                    a.status === "in_progress" ? "text-[#0284c7] font-semibold" :
                    a.status === "deferred" ? "text-[#94a3b8]" :
                    "text-[#b91c1c] font-semibold"
                  }>
                    {a.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Meetings */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white">
        <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#0284c7]" />
          <div className="font-heading text-base font-bold text-[#0f172a] flex-1">Meetings</div>
          <Button size="sm" onClick={() => setShowMeetingForm(true)} data-testid="committee-schedule-meeting" className="bg-[#0284c7] hover:bg-[#0369a1] text-white">
            <Plus className="w-3.5 h-3.5 mr-1" /> Schedule new meeting
          </Button>
        </div>
        <div className="divide-y divide-[#f1f5f9]">
          {meetings.length === 0 && (
            <div className="p-6 text-center text-sm text-[#475569] italic">
              No meetings yet. Schedule the first one — the agenda will auto-populate from the template
              {committee.auto_inject_quality && " and pre-fill with live Quality Command data"}.
            </div>
          )}
          {meetings.map((m) => (
            <button
              key={m.id}
              onClick={() => onOpenMeeting(m.id)}
              className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#f0f9ff] transition-colors duration-200"
              data-testid={`meeting-row-${m.id}`}
            >
              <div className="flex-1">
                <div className="font-semibold text-[#0f172a]">
                  {m.scheduled_at?.slice(0, 10)} {m.location && `· ${m.location}`}
                </div>
                <div className="text-xs text-[#475569] mt-0.5 capitalize">
                  Status: {m.status.replace("_", " ")}
                  {m.minutes_approved_by && ` · minutes approved by ${m.minutes_approved_by}`}
                </div>
              </div>
              <ChevronLeft className="w-4 h-4 rotate-180 text-[#94a3b8]" />
            </button>
          ))}
        </div>
      </div>

      <MemberFormDialog
        open={showMemberForm}
        onOpenChange={setShowMemberForm}
        committeeId={committee.id}
        defaultRoles={committee.default_member_roles || []}
        onSaved={load}
      />
      <MeetingFormDialog
        open={showMeetingForm}
        onOpenChange={setShowMeetingForm}
        committeeId={committee.id}
        onSaved={() => { load(); onReload(); }}
      />
    </div>
  );
}

function MemberRow({ member, committeeId, onSaved }) {
  const remove = async () => {
    if (!window.confirm(`Remove ${member.name}?`)) return;
    await Committees.deleteMember(committeeId, member.id);
    toast.success("Member removed");
    onSaved();
  };
  return (
    <div className="p-3 flex items-center gap-3 text-sm" data-testid={`member-row-${member.id}`}>
      <div className="flex-1">
        <div className="font-semibold text-[#0f172a] flex items-center gap-2">
          {member.name}
          {member.is_chair && <span className="text-[9px] uppercase tracking-widest bg-[#0284c7] text-white px-1.5 py-0.5 rounded">Chair</span>}
          {!member.voting && <span className="text-[9px] uppercase tracking-widest bg-[#f1f5f9] text-[#475569] px-1.5 py-0.5 rounded">Non-voting</span>}
        </div>
        <div className="text-xs text-[#475569]">{member.role_title}{member.email ? ` · ${member.email}` : ""}</div>
      </div>
      <Button variant="ghost" size="sm" onClick={remove} className="text-[#b91c1c]" data-testid={`member-remove-${member.id}`}>
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

function MemberFormDialog({ open, onOpenChange, committeeId, defaultRoles, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [isChair, setIsChair] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    await Committees.createMember(committeeId, {
      name: name.trim(), role_title: role.trim(), email: email.trim(), is_chair: isChair,
    });
    toast.success("Member added");
    setName(""); setRole(""); setEmail(""); setIsChair(false);
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="member-form">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="member-name" placeholder="e.g. Dr. Jane Smith" />
          </div>
          <div>
            <Label className="text-xs">Role / Title</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} data-testid="member-role" list="role-suggestions" placeholder="e.g. Medical Director" />
            <datalist id="role-suggestions">
              {defaultRoles.map((r) => <option key={r} value={r} />)}
            </datalist>
          </div>
          <div>
            <Label className="text-xs">Email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="member-email" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isChair} onChange={(e) => setIsChair(e.target.checked)} data-testid="member-is-chair" />
            Committee chair
          </label>
          <Button onClick={submit} data-testid="member-submit" className="w-full bg-[#0284c7] hover:bg-[#0369a1] text-white">
            Add member
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MeetingFormDialog({ open, onOpenChange, committeeId, onSaved }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const submit = async () => {
    if (!date) return toast.error("Meeting date required");
    const scheduled = `${date}T${time || "10:00"}:00Z`;
    const m = await Committees.createMeeting(committeeId, { scheduled_at: scheduled, location: location.trim() });
    toast.success("Meeting scheduled — agenda auto-populated");
    setDate(""); setLocation("");
    onSaved(m);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="meeting-form">
        <DialogHeader>
          <DialogTitle>Schedule new meeting</DialogTitle>
          <DialogDescription className="text-xs">Agenda will auto-populate from the committee template.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="meeting-date" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="meeting-time" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Location (optional)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="meeting-location" placeholder="e.g. Conference Room A" />
          </div>
          <Button onClick={submit} data-testid="meeting-submit" className="w-full bg-[#0284c7] hover:bg-[#0369a1] text-white">
            Schedule
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
function MinutesList({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#475569] mb-2">{title}</div>
      <ul className="space-y-1.5">
        {(items || []).length === 0 && <li className="text-xs text-[#94a3b8] italic">(none)</li>}
        {(items || []).map((it, i) => (
          <li key={i} className="text-xs text-[#0f172a] leading-relaxed flex gap-1.5">
            <span className="text-[#475569]">•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MeetingWorkspace({ committee, meeting, onReload }) {
  const [showActionForm, setShowActionForm] = useState(false);
  const [minutes, setMinutes] = useState(meeting.ai_minutes || null);
  const [minutesLoading, setMinutesLoading] = useState(false);
  const [minutesExpanded, setMinutesExpanded] = useState(false);
  const presentCount = meeting.attendance.filter((a) => a.present).length;

  useEffect(() => {
    setMinutes(meeting.ai_minutes || null);
  }, [meeting.id, meeting.ai_minutes]);

  const generateMinutes = async (refresh = false) => {
    setMinutesLoading(true);
    try {
      const r = await Committees.generateAiMinutes(committee.id, meeting.id, refresh);
      setMinutes(r.minutes);
      setMinutesExpanded(true);
      toast.success(r.cached ? "Loaded cached draft" : "AI draft ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to draft minutes");
    } finally {
      setMinutesLoading(false);
    }
  };

  const setStatus = async (status) => {
    const now = new Date().toISOString();
    const payload = { status };
    if (status === "in_progress" && !meeting.called_to_order_at) payload.called_to_order_at = now;
    if (status === "completed" && !meeting.adjourned_at) payload.adjourned_at = now;
    await Committees.updateMeeting(committee.id, meeting.id, payload);
    toast.success(`Status: ${status.replace("_", " ")}`);
    onReload();
  };

  return (
    <div className="p-6 space-y-4" data-testid="meeting-workspace">
      {/* Header row */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 flex flex-wrap items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Meeting</div>
          <div className="font-heading text-lg text-[#0f172a] tracking-tight">
            {meeting.scheduled_at?.slice(0, 10)} · {meeting.location || "—"}
          </div>
          <div className="text-xs text-[#475569] mt-0.5">
            Status: <span className="capitalize font-semibold">{meeting.status.replace("_", " ")}</span> · Present {presentCount}/{meeting.attendance.length}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          {meeting.status === "scheduled" && (
            <Button size="sm" variant="outline" onClick={() => setStatus("in_progress")} data-testid="meeting-call-to-order">Call to order</Button>
          )}
          {meeting.status === "in_progress" && (
            <Button size="sm" variant="outline" onClick={() => setStatus("completed")} data-testid="meeting-adjourn">Adjourn</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => downloadAuthed(Committees.meetingDocxUrl(committee.id, meeting.id), `${committee.short_name || "meeting"}-${meeting.scheduled_at?.slice(0,10)}.docx`)} data-testid="meeting-export-docx" className="border-[#e2e8f0]">
            <Download className="w-3.5 h-3.5 mr-1" /> Word packet
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadAuthed(Committees.meetingPdfUrl(committee.id, meeting.id), `${committee.short_name || "meeting"}-${meeting.scheduled_at?.slice(0,10)}.pdf`)} data-testid="meeting-export-pdf" className="border-[#e2e8f0]">
            <FileText className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button size="sm" onClick={() => generateMinutes(!!minutes)} disabled={minutesLoading} data-testid="meeting-ai-minutes" className="bg-[#0284c7] hover:bg-[#0369a1] text-white">
            <Sparkles className={`w-3.5 h-3.5 mr-1 ${minutesLoading ? "animate-pulse" : ""}`} />
            {minutesLoading ? "Drafting…" : minutes ? "Regenerate AI Minutes" : "AI Draft Minutes"}
          </Button>
        </div>
      </div>

      {minutes && (
        <div className="rounded-xl border border-[#e2e8f0] border-t-2 border-t-[#38bdf8] bg-white shadow-sm p-4 space-y-3" data-testid="ai-minutes-card">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#0284c7]" />
            <div className="font-heading text-base font-bold text-[#0f172a] tracking-tight flex-1">
              AI Draft Minutes
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#94a3b8] font-medium">
                {minutes.model === "fallback" ? "Deterministic" : minutes.model}
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setMinutesExpanded(!minutesExpanded)} className="text-[#475569]" data-testid="ai-minutes-toggle">
              {minutesExpanded ? "Hide details" : "Show details"}
            </Button>
            <Button size="sm" variant="ghost" onClick={async () => {
              await navigator.clipboard.writeText(minutes.formal_minutes || "");
              toast.success("Minutes copied to clipboard");
            }} data-testid="ai-minutes-copy" className="text-[#0284c7]">
              Copy formal minutes
            </Button>
          </div>
          <p className="text-sm text-[#0f172a] leading-relaxed" data-testid="ai-minutes-summary">{minutes.executive_summary}</p>
          {minutesExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-[#f1f5f9]">
              <MinutesList title="Key discussions" items={minutes.key_discussions} testid="ai-key-discussions" />
              <MinutesList title="Decisions" items={minutes.decisions} testid="ai-decisions" />
              <div data-testid="ai-action-items">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-[#475569] mb-2">Action items</div>
                <ul className="space-y-1.5">
                  {(minutes.action_items || []).length === 0 && <li className="text-xs text-[#94a3b8] italic">(none)</li>}
                  {(minutes.action_items || []).map((it, i) => (
                    <li key={i} className="text-xs text-[#0f172a] flex gap-1.5">
                      <span className="text-[#475569]">•</span>
                      <span>
                        <b>{it.title || it}</b>
                        {(it.owner || it.due) && (
                          <span className="text-[#475569]"> — {it.owner || "owner?"} · due {it.due || "—"}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {minutesExpanded && minutes.formal_minutes && (
            <details className="mt-2">
              <summary className="text-xs text-[#0284c7] cursor-pointer font-semibold">Show full formal minutes</summary>
              <pre className="mt-2 text-xs whitespace-pre-wrap font-sans leading-relaxed text-[#0f172a] bg-[#f8fafc] border border-[#e2e8f0] rounded p-3 max-h-[400px] overflow-auto">{minutes.formal_minutes}</pre>
            </details>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Agenda */}
        <div className="space-y-2" data-testid="meeting-agenda">
          <div className="flex items-center gap-2 px-1">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#475569] flex-1">
              Agenda ({meeting.agenda_sections.length} sections)
            </div>
            <Button size="sm" variant="outline" onClick={async () => {
              const title = window.prompt("New section title:");
              if (!title?.trim()) return;
              await Committees.addSection(committee.id, meeting.id, { title: title.trim() });
              toast.success("Section added");
              onReload();
            }} data-testid="add-section-btn" className="border-[#e2e8f0]">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add section
            </Button>
          </div>
          {meeting.agenda_sections.map((section, idx) => (
            <AgendaSectionEditor
              key={section.id}
              index={idx + 1}
              section={section}
              committeeId={committee.id}
              meetingId={meeting.id}
              onSaved={onReload}
            />
          ))}
        </div>

        {/* Attendance + Action items sidebar */}
        <div className="space-y-4">
          <AttendancePanel meeting={meeting} committeeId={committee.id} onReload={onReload} />
          <ActionItemsPanel
            committee={committee}
            meeting={meeting}
            onOpenAdd={() => setShowActionForm(true)}
            onReload={onReload}
          />
        </div>
      </div>

      <ActionItemFormDialog
        open={showActionForm}
        onOpenChange={setShowActionForm}
        committee={committee}
        meeting={meeting}
        onSaved={onReload}
      />
    </div>
  );
}

function AgendaSectionEditor({ index, section, committeeId, meetingId, onSaved }) {
  const [expanded, setExpanded] = useState(Boolean(section.notes) || Boolean(section.decision));
  const [notes, setNotes] = useState(section.notes || "");
  const [decision, setDecision] = useState(section.decision || "");
  const saveNotes = async () => {
    await Committees.updateSection(committeeId, meetingId, {
      section_id: section.id, notes, decision,
    });
    toast.success("Section saved");
    onSaved();
  };
  const rename = async () => {
    const title = window.prompt("Rename section:", section.title);
    if (!title?.trim() || title.trim() === section.title) return;
    await Committees.renameSection(committeeId, meetingId, section.id, title.trim());
    toast.success("Renamed");
    onSaved();
  };
  const remove = async () => {
    if (!window.confirm(`Delete section "${section.title}"? This cannot be undone.`)) return;
    await Committees.deleteSection(committeeId, meetingId, section.id);
    toast.success("Section deleted");
    onSaved();
  };
  const addItem = async () => {
    const title = window.prompt("New sub-item title:");
    if (!title?.trim()) return;
    await Committees.addItem(committeeId, meetingId, section.id, title.trim());
    toast.success("Sub-item added");
    onSaved();
  };
  const share = async () => {
    const label = window.prompt(`Share "${section.title}" — who is this for? (e.g. "Nursing report")`);
    if (label === null) return; // cancelled
    const link = await Committees.createSectionShare(committeeId, meetingId, section.id, label.trim());
    const url = Committees.sharedCommitteeUrl(link.token);
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Share link copied — department contributors can now fill this section.");
  };
  const hasContent = notes.trim() || decision.trim() || (section.items || []).some((it) => it.notes || it.decision);
  return (
    <div className="rounded-lg border border-[#e2e8f0] bg-white" data-testid={`agenda-section-${section.id}`}>
      <div className="flex items-center gap-1 px-1">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 px-3 py-2.5 flex items-center gap-2 hover:bg-[#f8fafc] text-left transition-colors duration-200 rounded"
        >
          <span className="text-xs font-mono text-[#475569] w-6">{index}.</span>
          <span className="font-semibold text-[#0f172a] flex-1">{section.title}</span>
          {hasContent && <CheckCircle2 className="w-4 h-4 text-[#16a34a]" />}
          <ChevronLeft className={`w-4 h-4 text-[#94a3b8] transition-transform ${expanded ? "-rotate-90" : "rotate-180"}`} />
        </button>
        <button onClick={share} data-testid={`section-share-${section.id}`} className="p-1.5 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded" title="Share for dept contribution">
          <Users className="w-3.5 h-3.5" />
        </button>
        <button onClick={rename} data-testid={`section-rename-${section.id}`} className="p-1.5 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded" title="Rename">
          <Settings2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={remove} data-testid={`section-delete-${section.id}`} className="p-1.5 text-[#475569] hover:text-[#b91c1c] hover:bg-[#fef2f2] rounded" title="Delete">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#f1f5f9] pt-3">
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Discussion / Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={saveNotes}
              rows={Math.max(3, (notes.match(/\n/g) || []).length + 2)}
              className="mt-1 text-sm border-[#e2e8f0]"
              placeholder="Enter discussion summary, findings, data reviewed…"
              data-testid={`section-notes-${section.id}`}
            />
          </div>
          <div>
            <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Section decision (optional)</Label>
            <Input
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              onBlur={saveNotes}
              className="mt-1 text-sm border-[#e2e8f0]"
              placeholder="e.g. Approved / Deferred / Referred to MEC"
              data-testid={`section-decision-${section.id}`}
            />
          </div>
          {(section.items || []).length > 0 && (
            <div className="pl-3 border-l-2 border-[#e2e8f0] space-y-2">
              {section.items.map((it, j) => (
                <SubItemEditor
                  key={it.id}
                  parentIndex={index}
                  index={j + 1}
                  item={it}
                  committeeId={committeeId}
                  meetingId={meetingId}
                  sectionId={section.id}
                  onSaved={onSaved}
                />
              ))}
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={addItem} data-testid={`section-add-item-${section.id}`} className="text-[#0284c7]">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add sub-item
          </Button>
        </div>
      )}
    </div>
  );
}

function SubItemEditor({ parentIndex, index, item, committeeId, meetingId, sectionId, onSaved }) {
  const [notes, setNotes] = useState(item.notes || "");
  const save = async () => {
    await Committees.updateSection(committeeId, meetingId, {
      section_id: sectionId, item_id: item.id, notes,
    });
    onSaved();
  };
  return (
    <div className="py-1" data-testid={`agenda-item-${item.id}`}>
      <div className="text-[13px] font-semibold text-[#0f172a] flex items-baseline gap-2">
        <span className="text-xs font-mono text-[#475569]">{parentIndex}.{index}</span>
        <span className="flex-1">{item.title}</span>
      </div>
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={save}
        rows={2}
        className="mt-1 text-xs border-[#e2e8f0]"
        placeholder="Notes / discussion / data reviewed"
        data-testid={`subitem-notes-${item.id}`}
      />
    </div>
  );
}

function AttendancePanel({ meeting, committeeId, onReload }) {
  const [guestName, setGuestName] = useState("");
  const toggle = async (a, present) => {
    await Committees.updateAttendance(committeeId, meeting.id, [
      { member_id: a.member_id, name: a.name, role_title: a.role_title, present, guest: a.guest || false },
    ]);
    onReload();
  };
  const addGuest = async () => {
    if (!guestName.trim()) return;
    await Committees.updateAttendance(committeeId, meeting.id, [
      { member_id: null, name: guestName.trim(), role_title: "Guest", present: true, guest: true },
    ]);
    setGuestName("");
    toast.success("Guest added");
    onReload();
  };
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white" data-testid="attendance-panel">
      <div className="px-4 py-2.5 border-b border-[#e2e8f0]">
        <div className="font-heading text-sm font-bold text-[#0f172a]">Sign-in / attendance</div>
      </div>
      <div className="p-3 space-y-1.5 max-h-[280px] overflow-auto">
        {meeting.attendance.map((a) => (
          <label key={(a.member_id || a.name) + "-" + a.role_title} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#f8fafc] rounded px-1 py-0.5">
            <input
              type="checkbox"
              checked={a.present}
              onChange={(e) => toggle(a, e.target.checked)}
              data-testid={`attend-${(a.member_id || a.name).toString().slice(0,10)}`}
            />
            <span className={a.present ? "font-semibold text-[#0f172a]" : "text-[#475569]"}>
              {a.name}
              {a.guest && <span className="ml-1 text-[9px] uppercase tracking-widest bg-[#f1f5f9] text-[#475569] px-1 py-0.5 rounded">Guest</span>}
            </span>
            <span className="text-[10px] text-[#94a3b8] ml-auto">{a.role_title}</span>
          </label>
        ))}
      </div>
      <div className="p-3 border-t border-[#f1f5f9] flex gap-2">
        <Input
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="Add guest…"
          className="text-sm h-8"
          data-testid="guest-name"
        />
        <Button size="sm" variant="outline" onClick={addGuest} data-testid="guest-add" className="border-[#e2e8f0]">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ActionItemsPanel({ committee, meeting, onOpenAdd, onReload }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    Committees.listActionItems(committee.id).then(setItems);
  }, [committee.id, meeting.updated_at]);
  const toggleStatus = async (a) => {
    const next = a.status === "completed" ? "open" : "completed";
    await Committees.updateActionItem(committee.id, a.id, { status: next });
    toast.success(`Action item ${next}`);
    onReload();
    setItems(await Committees.listActionItems(committee.id));
  };
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white" data-testid="action-items-panel">
      <div className="px-4 py-2.5 border-b border-[#e2e8f0] flex items-center">
        <div className="font-heading text-sm font-bold text-[#0f172a] flex-1">Action items</div>
        <Button size="sm" variant="ghost" onClick={onOpenAdd} data-testid="action-add-btn" className="text-[#0284c7]">
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="p-3 space-y-2 max-h-[280px] overflow-auto">
        {items.length === 0 && <div className="text-xs text-[#475569] italic">No action items yet.</div>}
        {items.map((a) => (
          <div key={a.id} className="text-xs" data-testid={`action-${a.id}`}>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={a.status === "completed"}
                onChange={() => toggleStatus(a)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className={a.status === "completed" ? "line-through text-[#94a3b8]" : "text-[#0f172a] font-semibold"}>
                  {a.title}
                </div>
                <div className="text-[#475569]">{a.owner_name || "—"} · due {a.due_date || "—"}</div>
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionItemFormDialog({ open, onOpenChange, committee, meeting, onSaved }) {
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    await Committees.createActionItem(committee.id, meeting.id, {
      title: title.trim(), owner_name: owner.trim(), due_date: due,
    });
    toast.success("Action item added");
    setTitle(""); setOwner(""); setDue("");
    onSaved();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="action-form">
        <DialogHeader>
          <DialogTitle>New action item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="action-title" placeholder="e.g. Complete restraint audit" />
          </div>
          <div>
            <Label className="text-xs">Owner</Label>
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="action-owner" placeholder="Assigned to" />
          </div>
          <div>
            <Label className="text-xs">Due date</Label>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="action-due" />
          </div>
          <Button onClick={submit} data-testid="action-submit" className="w-full bg-[#0284c7] hover:bg-[#0369a1] text-white">
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FacilitySettingsDialog({ open, onOpenChange, facility, onSaved }) {
  const [name, setName] = useState(facility.facility_name || "");
  const [address, setAddress] = useState(facility.facility_address || "");
  const [fy, setFy] = useState(facility.fiscal_year_label || "");
  useEffect(() => {
    setName(facility.facility_name || "");
    setAddress(facility.facility_address || "");
    setFy(facility.fiscal_year_label || "");
  }, [facility]);
  const save = async () => {
    await Committees.updateFacility({
      facility_name: name.trim(), facility_address: address.trim(), fiscal_year_label: fy.trim(),
    });
    toast.success("Facility saved");
    onSaved();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="facility-settings">
        <DialogHeader>
          <DialogTitle>Facility settings</DialogTitle>
          <DialogDescription className="text-xs">
            Appears on every exported Word packet and PDF. Only you can see this.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Facility name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="facility-name" placeholder="e.g. Northwest Behavioral Health" />
          </div>
          <div>
            <Label className="text-xs">Address (optional)</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="facility-address" />
          </div>
          <div>
            <Label className="text-xs">Fiscal year label (optional)</Label>
            <Input value={fy} onChange={(e) => setFy(e.target.value)} data-testid="facility-fy" placeholder="e.g. 2026" />
          </div>
          <Button onClick={save} data-testid="facility-save" className="w-full bg-[#0284c7] hover:bg-[#0369a1] text-white">
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
